import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Upload,
  X,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Layers,
  FileImage,
  ExternalLink,
  Trash2,
  ScanLine,
  Clock,
  Eye,
} from "lucide-react";
import { api } from "@/lib/api";
import ExamPreviewModal from "@/components/ExamPreviewModal";
import ReportStandardSelect from "@/components/ReportStandardSelect";

const MAX_FILES = 200;

export default function BatchUpload() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [examDate, setExamDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [reportStandard, setReportStandard] = useState("general");

  const [uploading, setUploading] = useState(false);
  const [batch, setBatch] = useState(null);
  const [previewIdx, setPreviewIdx] = useState(null);

  const doneExamIds = batch
    ? batch.items.filter((it) => it.status === "done" && it.exam_id).map((it) => it.exam_id)
    : [];

  const onFiles = (list) => {
    const imgs = Array.from(list).filter((f) => {
      const name = (f.name || "").toLowerCase();
      const isDicom = name.endsWith(".dcm") || name.endsWith(".dicom") || f.type === "application/dicom";
      return isDicom || f.type.startsWith("image/");
    });
    if (imgs.length === 0) {
      toast.error("Tidak ada file gambar yang valid");
      return;
    }
    const combined = [...files, ...imgs].slice(0, MAX_FILES);
    if (files.length + imgs.length > MAX_FILES) {
      toast.warning(`Maksimum ${MAX_FILES} file, sisanya diabaikan`);
    }
    setFiles(combined);
  };

  const removeFile = (i) => {
    const next = [...files];
    next.splice(i, 1);
    setFiles(next);
  };

  const clearAll = () => setFiles([]);

  const submit = async () => {
    if (files.length === 0) {
      toast.error("Pilih file terlebih dahulu");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("exam_date", examDate);
    fd.append("notes", notes);
    fd.append("report_standard", reportStandard);

    try {
      const { data } = await api.post("/batches", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      setBatch(data);
      toast.success(`Batch dimulai · ${data.total} file antre diproses`);
    } catch (e) {
      toast.error("Upload gagal", {
        description: String(e?.response?.data?.detail || e.message).slice(0, 200),
      });
    } finally {
      setUploading(false);
    }
  };

  // Poll batch status
  useEffect(() => {
    if (!batch || ["done", "failed"].includes(batch.status)) {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/batches/${batch.id}`);
        setBatch(data);
        if (["done", "failed"].includes(data.status)) {
          clearInterval(pollRef.current);
          toast.success(
            `Batch selesai · ${data.done}/${data.total} berhasil${data.failed ? ` · ${data.failed} gagal` : ""}`
          );
        }
      } catch (e) {
        // ignore
      }
    }, 2000);
    return () => clearInterval(pollRef.current);
  }, [batch]);

  const resetBatch = () => {
    setBatch(null);
    setFiles([]);
  };

  const deleteBatch = async () => {
    if (!batch) return;
    if (!window.confirm("Hapus batch ini beserta semua pemeriksaan yang dibuat?")) return;
    try {
      await api.delete(`/batches/${batch.id}?delete_exams=true`);
      toast.success("Batch dihapus");
      resetBatch();
    } catch (e) {
      toast.error("Gagal hapus");
    }
  };

  const progress = batch ? ((batch.done + batch.failed) / batch.total) * 100 : 0;

  return (
    <div className="p-6 sm:p-8 lg:p-10 max-w-[1400px] mx-auto">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold mb-2">
            Worklist
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-50">
            Batch Upload X-ray
          </h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Upload beberapa X-ray sekaligus (drag-drop folder). Nama file akan jadi{" "}
            <code className="text-indigo-300 font-mono text-xs">Patient ID</code>{" "}
            secara otomatis · maksimum {MAX_FILES} file per batch.
          </p>
        </div>
        <Link
          to="/batches"
          data-testid="link-batch-history"
          className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-100 px-3 py-2 rounded-lg border border-zinc-800 hover:bg-zinc-900"
        >
          <Clock className="w-3.5 h-3.5" /> Riwayat Batch
        </Link>
      </div>

      {/* Live batch view */}
      {batch && (
        <div className="mb-8 rounded-xl border border-zinc-900 bg-zinc-900/40" data-testid="live-batch-panel">
          <div className="px-5 py-4 border-b border-zinc-900 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold">
                Progress
              </div>
              <div className="font-display text-xl font-medium text-zinc-100 mt-0.5">
                {batch.done + batch.failed} / {batch.total} selesai
                {batch.failed > 0 && (
                  <span className="text-rose-400 text-sm ml-2">· {batch.failed} gagal</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {["done", "failed"].includes(batch.status) ? (
                <>
                  <button
                    onClick={resetBatch}
                    data-testid="new-batch-btn"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white px-3 py-1.5 text-xs font-medium"
                  >
                    <Upload className="w-3.5 h-3.5" /> Batch Baru
                  </button>
                  <button
                    onClick={deleteBatch}
                    data-testid="delete-batch-btn"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-rose-500/20 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 px-3 py-1.5 text-xs font-medium"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Hapus
                  </button>
                </>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-xs text-indigo-300">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Memproses...
                </span>
              )}
            </div>
          </div>
          <div className="h-2 bg-zinc-800">
            <div
              className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="max-h-[500px] overflow-y-auto divide-y divide-zinc-900">
            {batch.items.map((it, i) => (
              <BatchItemRow
                key={it.item_id}
                item={it}
                onOpen={(id) => navigate(`/exam/${id}`)}
                onPreview={
                  it.exam_id
                    ? () => {
                        const idx = doneExamIds.indexOf(it.exam_id);
                        if (idx >= 0) setPreviewIdx(idx);
                      }
                    : null
                }
              />
            ))}
          </div>
        </div>
      )}

      {previewIdx !== null && doneExamIds.length > 0 && (
        <ExamPreviewModal
          examIds={doneExamIds}
          currentIndex={previewIdx}
          onClose={() => setPreviewIdx(null)}
          onIndexChange={setPreviewIdx}
        />
      )}

      {/* Uploader */}
      {!batch && (
        <>
          {/* Batch settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold block mb-1.5">
                Tanggal Exam (untuk semua file)
              </label>
              <input
                type="date"
                value={examDate}
                onChange={(e) => setExamDate(e.target.value)}
                data-testid="batch-exam-date"
                className="block w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold block mb-1.5">
                Catatan Klinis (untuk semua file)
              </label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Batch MCU PT Sehat Selalu · Feb 2026"
                data-testid="batch-notes"
                className="block w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <ReportStandardSelect value={reportStandard} onChange={setReportStandard} testId="batch-report-standard" />
            </div>
          </div>

          {/* Dropzone */}
          {files.length === 0 ? (
            <div
              data-testid="batch-dropzone"
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                onFiles(e.dataTransfer.files);
              }}
              onClick={() => fileRef.current?.click()}
              className={`shine cursor-pointer group flex flex-col items-center justify-center rounded-2xl border-2 border-dashed transition-all min-h-[400px] ${
                dragOver
                  ? "border-indigo-500 bg-indigo-500/5"
                  : "border-zinc-800 hover:border-indigo-500/60 bg-zinc-900/30"
              }`}
            >
              <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center mb-5 group-hover:bg-indigo-500/20">
                <Layers className="w-6 h-6 text-indigo-400" strokeWidth={1.75} />
              </div>
              <div className="font-display text-xl font-medium text-zinc-100 mb-1">
                Drop folder X-ray atau klik untuk pilih
              </div>
              <div className="text-sm text-zinc-500 mb-6">
                PNG · JPG · WEBP · DICOM (.dcm) · sampai {MAX_FILES} file · 3 diproses paralel
              </div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-600 font-mono">
                Filename → auto Patient ID
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*,.dcm,application/dicom"
                multiple
                className="hidden"
                onChange={(e) => onFiles(e.target.files)}
                data-testid="batch-file-input"
              />
            </div>
          ) : (
            <div className="rounded-xl border border-zinc-900 bg-zinc-900/40">
              <div className="px-5 py-3 border-b border-zinc-900 flex items-center justify-between">
                <div className="text-sm">
                  <span className="text-zinc-100 font-medium">{files.length}</span>
                  <span className="text-zinc-500"> file dipilih</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => fileRef.current?.click()}
                    data-testid="batch-add-more-btn"
                    className="text-xs text-indigo-400 hover:text-indigo-300"
                  >
                    + Tambah file
                  </button>
                  <button
                    onClick={clearAll}
                    data-testid="batch-clear-btn"
                    className="text-xs text-zinc-500 hover:text-rose-400"
                  >
                    Clear all
                  </button>
                </div>
              </div>
              <div className="max-h-[400px] overflow-y-auto divide-y divide-zinc-900">
                {files.map((f, i) => (
                  <div key={i} className="px-5 py-2.5 flex items-center gap-3">
                    <FileImage className="w-4 h-4 text-zinc-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-zinc-100 truncate">{f.name}</div>
                      <div className="text-[10px] font-mono text-zinc-500">
                        Patient ID: {f.name.replace(/\.[^.]+$/, "")} · {(f.size / 1024).toFixed(0)} KB
                      </div>
                    </div>
                    <button
                      onClick={() => removeFile(i)}
                      className="p-1 text-zinc-500 hover:text-rose-400"
                      data-testid={`remove-file-${i}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="px-5 py-4 border-t border-zinc-900">
                <button
                  onClick={submit}
                  disabled={uploading}
                  data-testid="submit-batch-btn"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white px-5 py-3 text-sm font-medium shadow-lg shadow-indigo-500/20 transition-colors"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Mengupload {files.length} file...
                    </>
                  ) : (
                    <>
                      <ScanLine className="w-4 h-4" />
                      Mulai Batch Analysis ({files.length} file)
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function BatchItemRow({ item, onOpen, onPreview }) {
  const statusIcon = {
    pending: <Clock className="w-4 h-4 text-zinc-500" />,
    processing: <Loader2 className="w-4 h-4 text-indigo-400 animate-spin" />,
    done: <CheckCircle2 className="w-4 h-4 text-emerald-400" />,
    failed: <AlertCircle className="w-4 h-4 text-rose-400" />,
  }[item.status];

  const statusText = {
    pending: "Menunggu",
    processing: "Memproses...",
    done: `${item.findings ?? 0} temuan`,
    failed: item.error || "Gagal",
  }[item.status];

  return (
    <div
      className="px-5 py-3 flex items-center gap-3 hover:bg-zinc-900/60 transition-colors"
      data-testid={`batch-item-${item.item_id}`}
    >
      <div className="shrink-0">{statusIcon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-zinc-100 truncate">{item.filename}</div>
        <div className="text-[10px] font-mono text-zinc-500 truncate">
          {item.patient_id} · {statusText}
        </div>
      </div>
      {item.status === "done" && item.exam_id && (
        <div className="flex items-center gap-1 shrink-0">
          {onPreview && (
            <button
              onClick={onPreview}
              data-testid={`preview-batch-item-${item.item_id}`}
              className="inline-flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300 px-2 py-1 rounded hover:bg-indigo-500/10"
              title="Preview cepat"
            >
              <Eye className="w-3.5 h-3.5" /> Preview
            </button>
          )}
          <button
            onClick={() => onOpen(item.exam_id)}
            className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-100 px-2 py-1 rounded hover:bg-zinc-800"
            data-testid={`open-batch-exam-${item.item_id}`}
          >
            Buka <ExternalLink className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}
